/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { DateTime } from 'luxon';
import { RegistryAnalyticsInterval } from './analytics-types';
import {
  RangeWindow,
  earliestFrom,
  fieldsOf,
  formatBound,
  isMidnight,
  isRelative,
  precedingWindow,
  resolveBound,
  resolveWindow,
  windowOf
} from './date-expressions';

// The analytics period: what the range picker selects (a quick range or a custom span, plus a
// granularity) and the query period it resolves to. Bound spellings are date-expressions'.

// ---- Quick ranges -------------------------------------------------------------------------

interface QuickRangeSpec {
  value: string;
  label: string;
  /** The window the range covers, as expressions: a range left open stays true to its label. */
  window: RangeWindow;
  /** The granularity the range opens with. */
  interval: RegistryAnalyticsInterval;
}

/**
 * The quick ranges: rolling spans ending now, then calendar periods on UTC days and ISO weeks
 * (only the parts that have happened: a whole week would plot its future days as zeros). All
 * time covers the API's five-year window. The bounds are date-expressions, resolved on every
 * fetch, so "today so far" is still today's once the clock passes midnight.
 */
export const ANALYTICS_RANGES = [
  { value: '7d', label: 'Last 7 days', window: { from: 'now-7d', to: 'now' }, interval: '1h' },
  { value: '14d', label: 'Last 14 days', window: { from: 'now-14d', to: 'now' }, interval: '1h' },
  { value: '30d', label: 'Last 30 days', window: { from: 'now-30d', to: 'now' }, interval: '1h' },
  { value: '3mo', label: 'Last 3 months', window: { from: 'now-3M', to: 'now' }, interval: '12h' },
  { value: '6mo', label: 'Last 6 months', window: { from: 'now-6M', to: 'now' }, interval: 'day' },
  { value: '12mo', label: 'Last 12 months', window: { from: 'now-1y', to: 'now' }, interval: '2d' },
  { value: 'all', label: 'All time', window: { from: 'now-5y', to: 'now' }, interval: 'month' },
  { value: 'today', label: 'Today so far', window: { from: 'now/d', to: 'now' }, interval: '1h' },
  { value: 'yesterday', label: 'Yesterday', window: { from: 'now-1d/d', to: 'now/d' }, interval: '1h' },
  { value: 'this-week', label: 'This week so far', window: { from: 'now/w', to: 'now' }, interval: '1h' },
  { value: 'previous-week', label: 'Previous week', window: { from: 'now-1w/w', to: 'now/w' }, interval: '1h' },
  { value: 'this-month', label: 'This month so far', window: { from: 'now/M', to: 'now' }, interval: '1h' },
  { value: 'previous-month', label: 'Previous month', window: { from: 'now-1M/M', to: 'now/M' }, interval: '1h' },
  { value: 'this-year', label: 'This year so far', window: { from: 'now/y', to: 'now' }, interval: 'day' },
  { value: 'previous-year', label: 'Previous year', window: { from: 'now-1y/y', to: 'now/y' }, interval: 'day' }
] as const satisfies readonly QuickRangeSpec[];

type QuickRange = (typeof ANALYTICS_RANGES)[number];
export type AnalyticsRange = QuickRange['value'];

/** The range shown without a URL choice: analytics data only reaches a few months back. */
export const DEFAULT_RANGE: AnalyticsRange = '3mo';

export const isRange = (value: string | null): value is AnalyticsRange =>
  ANALYTICS_RANGES.some((option) => option.value === value);

const quickRange = (range: AnalyticsRange): QuickRange => ANALYTICS_RANGES.find((option) => option.value === range)!;

// ---- Selection ----------------------------------------------------------------------------

/** What the range picker holds: a quick range or a custom span, plus a granularity. */
export interface RangeSelection {
  range: AnalyticsRange | 'custom';
  /** Custom span start in typed form (see date-expressions); set when range is 'custom'. */
  from?: string;
  /** Custom span end in typed form, a date inclusive; set when range is 'custom'. */
  to?: string;
  /** Explicit granularity, overriding the span's default. */
  interval?: RegistryAnalyticsInterval;
}

// ---- Granularity --------------------------------------------------------------------------

interface IntervalTraits {
  hours: number;
  /** Picker wording ("Every 6 hours"). */
  label: string;
  /** Human words for one bucket ("6 hours"). */
  word: string;
}

/** Everything hanging off a granularity, one row per interval and finest first, so nothing can drift apart. */
export const INTERVALS: Record<RegistryAnalyticsInterval, IntervalTraits> = {
  '1h': { hours: 1, label: 'Hourly', word: 'hour' },
  '6h': { hours: 6, label: 'Every 6 hours', word: '6 hours' },
  '12h': { hours: 12, label: 'Every 12 hours', word: '12 hours' },
  day: { hours: 24, label: 'Daily', word: 'day' },
  '2d': { hours: 48, label: 'Every 2 days', word: '2 days' },
  week: { hours: 168, label: 'Weekly', word: 'week' },
  month: { hours: 720, label: 'Monthly', word: 'month' }
};

/** The granularities, finest first: the record's own order. */
const ALL_INTERVALS = Object.keys(INTERVALS) as RegistryAnalyticsInterval[];

export const isInterval = (value: string | null): value is RegistryAnalyticsInterval =>
  ALL_INTERVALS.includes(value as RegistryAnalyticsInterval);

/**
 * Mirrors the API's bucket cap per request. Sub-day buckets reach any past period, so this alone
 * bounds their span: 1h up to 33 days, 6h up to 200, 12h up to 400.
 */
const MAX_BUCKETS = 800;
/** Above this the default granularity steps up a size, keeping charts legible. */
const DEFAULT_MAX_BUCKETS = 200;
/** Spans up to a month read hourly by default: 31 days is 744 buckets, within the API's cap. */
const HOURLY_MAX_HOURS = 31 * 24;

/** Length of a window in hours, relative bounds resolved now. */
function windowHours(window: RangeWindow): number {
  const { start, end } = resolveWindow(window);
  return end.diff(start).as('hours');
}

/**
 * Granularities valid for a span of so many hours: at least two buckets (hourly, the finest,
 * also for shorter spans like "now-1h") and no more than the API's cap.
 */
function optionsFor(hours: number): RegistryAnalyticsInterval[] {
  return ALL_INTERVALS.filter((interval) => {
    const buckets = hours / INTERVALS[interval].hours;
    return (buckets >= 2 || interval === '1h') && buckets <= MAX_BUCKETS;
  });
}

/** Granularities valid for a window (see {@link optionsFor}). */
export const intervalOptions = (window: RangeWindow): RegistryAnalyticsInterval[] => optionsFor(windowHours(window));

/** The default granularity of a window: hourly up to a month, otherwise the finest that stays chart-legible. */
export function defaultInterval(window: RangeWindow): RegistryAnalyticsInterval {
  const hours = windowHours(window);
  const options = optionsFor(hours);
  if (hours <= HOURLY_MAX_HOURS && options.includes('1h')) {
    return '1h';
  }
  return (
    options.find((interval) => hours / INTERVALS[interval].hours <= DEFAULT_MAX_BUCKETS) ??
    options[options.length - 1] ??
    'month'
  );
}

// ---- Period -------------------------------------------------------------------------------

export interface AnalyticsPeriod {
  /** The span as requests send it; relative bounds resolve again on every fetch (see resolveWindow). */
  window: RangeWindow;
  /** Bucket size of every chart. */
  interval: RegistryAnalyticsInterval;
  /** Human words for one bucket ("hour"/"6 hours"/"2 days"). */
  bucketWord: string;
  /** Display phrase for the covered span ("last 7 days", "now-6h – now"). */
  phrase: string;
}

function buildPeriod(
  window: RangeWindow,
  interval: RegistryAnalyticsInterval | undefined,
  phrase: string
): AnalyticsPeriod {
  const resolved = interval ?? defaultInterval(window);
  return { window, interval: resolved, bucketWord: INTERVALS[resolved].word, phrase };
}

/** Maps a quick range to a concrete query period, at its own granularity. */
export function rangeToPeriod(range: AnalyticsRange): AnalyticsPeriod {
  const preset = quickRange(range);
  // the label in running text: "last 7 days", "this week so far"
  return buildPeriod(preset.window, preset.interval, preset.label.toLowerCase());
}

/**
 * Display phrase of a custom span: the expressions as typed when either follows the clock
 * ("now-6h – now"), which is what the span means; otherwise its days ("Jun 1 – Aug 20, 2026") or,
 * for spans not on whole days, its moments ("Jul 1, 06:00 – Jul 1, 18:00, 2026").
 */
function customPhrase(window: RangeWindow, now: DateTime): string {
  if (isRelative(window.from) || isRelative(window.to)) {
    const fields = fieldsOf(window);
    return `${fields.from} – ${fields.to}`;
  }
  const { start, end } = resolveWindow(window, now);
  if (isMidnight(start) && isMidnight(end)) {
    const last = end.minus({ days: 1 });
    return `${start.toFormat(start.year === last.year ? 'MMM d' : 'MMM d, yyyy')} – ${last.toFormat('MMM d, yyyy')}`;
  }
  return `${start.toFormat('MMM d, HH:mm')} – ${end.toFormat('MMM d, HH:mm, yyyy')}`;
}

/** Resolves a range selection (quick range or custom span, optional granularity) to a query period. */
export function periodFor(selection: RangeSelection, now: DateTime = DateTime.utc()): AnalyticsPeriod {
  const custom =
    selection.range === 'custom' && selection.from !== undefined && selection.to !== undefined
      ? windowOf({ from: selection.from, to: selection.to }, now)
      : undefined;
  if (custom !== undefined) {
    return buildPeriod(custom, selection.interval, customPhrase(custom, now));
  }
  const base = rangeToPeriod(selection.range === 'custom' ? DEFAULT_RANGE : selection.range);
  return selection.interval ? buildPeriod(base.window, selection.interval, base.phrase) : base;
}

/**
 * The period's span moved a whole length back (-1) or forward (1), keeping its granularity, or
 * undefined when that leaves the data: forward from a span already reaching now, back past the
 * API's window. A step forward that would run into the future stops at now. The result is fixed,
 * in typed form: days when the span sits on whole days, moments otherwise.
 */
export function shiftedSelection(
  period: AnalyticsPeriod,
  direction: -1 | 1,
  now: DateTime = DateTime.utc()
): RangeSelection | undefined {
  // relative bounds resolve to whole seconds, so a live span ends exactly here
  const limit = now.startOf('second');
  const { start, end } = resolveWindow(period.window, now);
  const span = end.diff(start);
  if (direction === 1 && end >= limit) {
    return undefined;
  }
  const nextEnd = DateTime.min(direction === 1 ? end.plus(span) : end.minus(span), limit);
  const nextStart = nextEnd.minus(span);
  if (nextStart < earliestFrom(now)) {
    return undefined;
  }
  return {
    range: 'custom',
    ...fieldsOf({ from: formatBound(nextStart), to: formatBound(nextEnd) }),
    interval: period.interval
  };
}

// ---- Requests -----------------------------------------------------------------------------

interface SeriesRequest extends RangeWindow {
  extensions: string[];
  agents: string[];
  interval: RegistryAnalyticsInterval;
}

/**
 * Params of the comparison's series request: one builder, so the compare view and the page
 * shell issue identical query keys and share the cache entry.
 */
export function compareSeriesRequest(period: AnalyticsPeriod, agents: string[], extensions: string[]): SeriesRequest {
  return { extensions, agents, interval: period.interval, ...period.window };
}

/**
 * The growth baseline's request: the span of equal length just before the bounds the current
 * series came back for, so however the two fetches fall against the clock they meet exactly. It
 * is only summed for growth figures; one request spanning both periods would breach the bucket
 * cap at fine granularities and come back coarser. Undefined when the baseline would start
 * before the API's window (all time).
 */
export function baselineRequest(
  current: SeriesRequest,
  fetched: RangeWindow,
  now: DateTime = DateTime.utc()
): SeriesRequest | undefined {
  const window = precedingWindow(fetched, now);
  return resolveBound(window.from, now) >= earliestFrom(now) ? { ...current, ...window } : undefined;
}

interface OverviewRequest extends RangeWindow {
  interval: RegistryAnalyticsInterval;
  agents: string[];
  namespaces: string[];
  category?: string;
}

/**
 * Params of the overview query: one builder, so the page shell and the overview sub-route issue
 * identical query keys and share the cache entry.
 */
export function overviewRequest(
  period: AnalyticsPeriod,
  agents: string[],
  namespaces: string[],
  category: string
): OverviewRequest {
  return {
    ...period.window,
    interval: period.interval,
    agents,
    namespaces,
    category: category === 'All' ? undefined : category
  };
}
